import { Outlet, Link, useLocation } from "react-router-dom";
import { FileText, Package, Users, LayoutDashboard, LogOut, Bell, Menu, X, Factory, Droplets, Scissors, BarChart3, Box, Layers, Sun, Moon, Wrench, ShoppingCart, ClipboardList, CalendarDays, Palette, IndianRupee } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { useTheme } from "./ThemeProvider";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  
  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };
  
  useEffect(() => {
    fetchUnreadCount();
    // Poll for unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  };
  
  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  const navItems = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/purchase-orders", label: "Purchase Orders", icon: FileText },
    { path: "/production-dashboard", label: "WIP Dashboard", icon: BarChart3 },
    { path: "/create-work-order", label: "Create Work Order", icon: Package },
    { path: "/weaving-production", label: "Weaving", icon: Factory },
    { path: "/yarn-requirement", label: "Yarn Requirement", icon: Layers, deptRestrict: "weaving" },
    { path: "/dyeing-production", label: "Dyeing", icon: Droplets },
    { path: "/stitching-production", label: "Stitching", icon: Scissors },
    { path: "/accessories", label: "Accessories", icon: Box },
    { path: "/online-sales", label: "Online Sales", icon: ShoppingCart, deptRestrict: "ecom" },
    { path: "/spare-parts", label: "Spare Parts", icon: Wrench, deptRestrict: "maintenance" },
    { path: "/color-store", label: "Color Store", icon: Palette },
    { path: "/accounts-dashboard", label: "Accounts", icon: IndianRupee },
    { path: "/vendors", label: "Vendors", icon: Users },
    { path: "/notifications", label: "Notifications", icon: Bell, badge: unreadCount },
  ];

  // Add User Management for admin only
  if (user?.role === 'admin') {
    navItems.splice(10, 0, { path: "/users", label: "User Management", icon: Users });
    // Add Yarn Admin for admin
    navItems.splice(6, 0, { path: "/yarn-requirement-admin", label: "Yarn Admin", icon: Layers });
  }
  
  // Add Pending Work ONLY for Divyang
  if (user?.username === 'Divyang') {
    navItems.push({ path: "/pending-work", label: "Pending Work", icon: ClipboardList });
  }
  
  // Department-based filtering
  const userDept = user?.department?.toLowerCase() || '';
  const isAdmin = user?.role === 'admin';
  const isMarketing = userDept === 'marketing';
  const isMaintenance = userDept === 'maintenance';
  const isAccessories = userDept === 'accessories' || userDept === 'stitching/accessories';
  const isAccounts = userDept === 'accounts';
  const isStitching = userDept === 'stitching';
  const isDyeing = userDept === 'dyeing';
  const isColorStore = userDept === 'color_store';
  const hasEcomAccess = isAdmin || isAccessories || isAccounts || isStitching || isDyeing;
  const hasColorStoreAccess = isAdmin || isColorStore || isAccounts || isDyeing;

  // Add Attendance for Divyang and Accounts only
  if (user?.username?.toLowerCase() === 'divyang' || isAccounts) {
    navItems.push({ path: "/attendance", label: "Attendance", icon: CalendarDays });
  }
  
  let filteredNavItems = navItems;
  
  if (isMaintenance) {
    // Maintenance: Dashboard, Purchase Orders, Spare Parts, Vendors, Notifications
    filteredNavItems = navItems.filter(item => 
      ['/', '/purchase-orders', '/spare-parts', '/vendors', '/notifications'].includes(item.path)
    );
  } else if (!isAdmin && !isMarketing) {
    // Non-admin/non-marketing: filter based on department
    filteredNavItems = navItems.filter(item => {
      // Hide Create Work Order from non-weaving users
      if (item.path === '/create-work-order' && userDept !== 'weaving') {
        return false;
      }
      // Hide Yarn Requirement from non-weaving users
      if (item.path === '/yarn-requirement' && userDept !== 'weaving') {
        return false;
      }
      // Hide Yarn Admin from non-admin users
      if (item.path === '/yarn-requirement-admin') {
        return false;
      }
      // Hide Spare Parts from non-maintenance/non-admin users
      if (item.path === '/spare-parts') {
        return false;
      }
      // Hide Online Sales from users without ECOM access
      if (item.path === '/online-sales' && !hasEcomAccess) {
        return false;
      }
      // Hide Color Store from users without color store access
      if (item.path === '/color-store' && !hasColorStoreAccess) {
        return false;
      }
      // Hide Accounts Dashboard from non-accounts/non-admin
      if (item.path === '/accounts-dashboard' && !isAccounts) {
        return false;
      }
      return true;
    });
  } else if (isMarketing) {
    // Marketing: Hide Yarn Requirement (both pages), Online Sales, and Spare Parts
    filteredNavItems = navItems.filter(item => {
      if (item.path === '/yarn-requirement' || item.path === '/yarn-requirement-admin') {
        return false;
      }
      if (item.path === '/online-sales') {
        return false;
      }
      // Marketing should NOT see Spare Parts (only Maintenance and Admin)
      if (item.path === '/spare-parts') {
        return false;
      }
      // Marketing should NOT see Color Store
      if (item.path === '/color-store') {
        return false;
      }
      // Marketing should NOT see Accounts Dashboard
      if (item.path === '/accounts-dashboard') {
        return false;
      }
      return true;
    });
  }
  // Admin gets all nav items (no additional filtering needed)

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <h1 className="font-heading font-bold text-lg text-primary">Order Factory</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-9 w-9"
            data-testid="theme-toggle-mobile"
          >
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-muted rounded-sm"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Sidebar - Desktop always visible, Mobile slide-in */}
      <aside className={`
        w-72 bg-card border-r border-border flex flex-col
        fixed lg:relative inset-y-0 left-0 z-40
        transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 border-b border-border hidden lg:flex lg:items-center lg:justify-between">
          <div>
            <h1 className="font-heading font-bold text-xl text-primary" data-testid="app-title">Order Factory</h1>
            <p className="text-sm font-semibold text-muted-foreground mt-1">Eurospa Terry Towels</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-9 w-9"
            data-testid="theme-toggle-desktop"
          >
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>
        </div>
        
        <nav className="flex-1 p-4 mt-16 lg:mt-0 overflow-y-auto">
          <ul className="space-y-2">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-sm transition-colors relative ${
                      active
                        ? "bg-primary text-primary-foreground border-l-4 border-primary"
                        : "text-foreground hover:bg-muted"
                    }`}
                  >
                    <Icon size={18} />
                    <span className="text-sm font-medium">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-destructive text-destructive-foreground rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-border">
          <div className="px-4 py-2 mb-3 bg-muted/50 rounded-sm">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Department</p>
            <p className="text-sm font-bold text-primary capitalize">{user?.department || 'General'}</p>
            {user?.role === 'admin' && (
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-bold bg-primary/10 text-primary rounded-full">
                Admin - Full Access
              </span>
            )}
            {user?.department === 'accounts' && user?.role !== 'admin' && (
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-bold bg-green-100 text-green-700 rounded-full">
                All POs Access
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold">
              {user?.full_name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.username}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-sm transition-colors"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <main className="flex-1 overflow-y-auto pt-16 lg:pt-0 px-4 lg:px-6 py-4 lg:py-6">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
